import { useTranslation } from 'react-i18next'

import { formatUsPhone, formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import {
  CargoInsuranceIcon,
  CloseSmallIcon,
  ShieldVerifiedIcon,
  SuperPayIcon,
} from '#/shared/ui/icons'

import { formatRequestedAgo } from '../model/format-ago'
import type { CarrierRequestCarrier } from '../model/types'

interface CarrierIdentityProps {
  carrier: CarrierRequestCarrier
  requestedAt: string
  onOpenCarrier?: () => void
}

export function CarrierIdentity({
  carrier,
  requestedAt,
  onOpenCarrier,
}: CarrierIdentityProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-sm">
        <CarrierName name={carrier.name} onOpen={onOpenCarrier} />{' '}
        <VerificationBadge isVerified={carrier.isVerified} />
      </div>
      <ContactRow contactName={carrier.contactName} phone={carrier.phone} />
      <InsuranceRow insurance={carrier.insurance} />
      {carrier.largestCapacityLabel && (
        <CapacityRow label={carrier.largestCapacityLabel} />
      )}
      <SuperPayRow
        acceptsSuperPay={carrier.acceptsSuperPay}
        requestedAt={requestedAt}
      />
    </div>
  )
}

function CarrierName({ name, onOpen }: { name: string; onOpen?: () => void }) {
  return (
    <span
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen()
              }
            }
          : undefined
      }
      className="cursor-pointer text-sm font-medium text-gray-900 uppercase underline underline-offset-2"
    >
      {name}
    </span>
  )
}

function VerificationBadge({ isVerified }: { isVerified: boolean }) {
  const { t } = useTranslation()
  const label = isVerified
    ? t('carrierRequests.verifiedBySd')
    : t('carrierRequests.unverifiedBySd')
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1.25 rounded-full px-1 align-middle text-xs font-medium whitespace-nowrap',
        isVerified ? 'bg-green-50 text-green-500' : 'bg-red-100 text-red-500',
      )}
    >
      {isVerified ? (
        <ShieldVerifiedIcon className="size-3.5 shrink-0" />
      ) : (
        <CloseSmallIcon className="size-3.5 shrink-0" />
      )}
      <span className="pr-0.5">{label}</span>
    </span>
  )
}

function ContactRow({
  contactName,
  phone,
}: {
  contactName?: string
  phone?: string
}) {
  if (!contactName && !phone) return null
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      {contactName && <span>{contactName}</span>}
      {contactName && phone && <span className="text-gray-300">·</span>}
      {phone && (
        <a href={`tel:${phone}`} className="text-primary hover:underline">
          {formatUsPhone(phone)}
        </a>
      )}
    </div>
  )
}

function InsuranceRow({
  insurance,
}: {
  insurance?: { limit: number; deductible: number }
}) {
  const { t } = useTranslation()
  if (!insurance) return null
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <CargoInsuranceIcon className="size-3.5 shrink-0" />
      <span className="text-primary underline">
        {t('carrierRequests.cargoInsurance')}
      </span>
      <span>
        {t('carrierRequests.insuranceLine', {
          limit: formatUsd(insurance.limit),
          deductible: formatUsd(insurance.deductible),
        })}
      </span>
    </div>
  )
}

function CapacityRow({ label }: { label: string }) {
  const { t } = useTranslation()
  return (
    <p className="text-xs text-gray-500">
      {t('carrierRequests.largestCapacity', { label })}
    </p>
  )
}

function SuperPayRow({
  acceptsSuperPay,
  requestedAt,
}: {
  acceptsSuperPay: boolean
  requestedAt: string
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3 text-xs text-gray-500">
      {acceptsSuperPay && (
        <span className="inline-flex items-center gap-1">
          {t('carrierRequests.accepts')}
          <SuperPayIcon
            aria-label={t('carrierRequests.superPay')}
            className="h-4 w-15 shrink-0"
          />
        </span>
      )}
      <span className="text-gray-300">
        {formatRequestedAgo(t, requestedAt)}
      </span>
    </div>
  )
}
